import {Button, Grid, makeStyles, Table, TableBody, TableCell, TableHead, TableRow, Dialog, DialogActions, DialogTitle, DialogContent, Typography} from '@material-ui/core';
import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { STATUS } from 'src/constants/demands';
import { useGoBack } from 'src/hooks/common';
import { useGetPdfDemand } from 'src/hooks/demands';
import { isDoctorSelected } from 'src/store/selectors/entity';

import {
  isAdmin as isAdminSelector,
  isSuperAdmin as isSuperAdminSelector,
  isAssistant as isAssistantSelector,
  isManager as isManagerSelector,
} from 'src/store/selectors/user';
import { downloadFile } from 'src/utils/downloadFile';
import format from 'date-fns/format';
import useCheckDataDemand from "src/hooks/demands/useCheckDataDemand";
import AlertDialog from "src/components/AlertDialog";

import { styled } from '@material-ui/core/styles';
import Loader from "src/components/Loader";


const useStyles = makeStyles((theme) => ({
  grid: {
    padding: '7px !important',
  },
  button: {
    marginRight: theme.spacing(2),
  },
}));

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialogContent-root': {
    padding: theme.spacing(2),
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(1),
  },
}));

const DemandDetailsActions = ({ demand, version }) => {
  const classes = useStyles();
  const { t } = useTranslation(['demands', 'common', 'rolesNames']);
  const { enqueueSnackbar } = useSnackbar();
  const isAssistant = useSelector(isAssistantSelector);
  const doctor = useSelector(isDoctorSelected);
  const isManager = useSelector(isManagerSelector);
  const isAdmin = useSelector(isAdminSelector);
  const isSuperAdmin = useSelector(isSuperAdminSelector);
  const [pdfRequest, setPdfRequest] = useState(false);
  const [isAddCommentButtonLoading, setIsAddCommentButtonLoading] = useState(false); // New state for button loading
  const goBack = useGoBack('/app/demands');

  const { data: demandPdf, isErrorPdf, refetch: refetchPdf } = useGetPdfDemand(
    demand?.id,
    version ? version.id : demand.versions[0]?.id,
    isAssistant ? doctor?.id : undefined,
  );

  const [open, setOpen] = useState(false);
  const { isLoading: isLoadingCheckDataDemand, data: dataCheckData, isError: isErrorCheckData, error: errorCheckData, refetch: refetchCheckDataDemand } = useCheckDataDemand(demand?.id);

  const generatePdf = () => {
    setPdfRequest(true);
    refetchPdf();
  };

  const handleClickOpen = async () => {
    try {
      setIsAddCommentButtonLoading(true); // Disable button and set loading text
      // await refetchCheckDataDemand(); // Fetch data when button is clicked
      setOpen(true); // Open the modal after fetching data
    } catch (error) {
      enqueueSnackbar('Error fetching data', { variant: 'error' });
    } finally {
      setIsAddCommentButtonLoading(false); 
    }
  };
  
  useEffect(() => {
    if (isErrorCheckData) {
      enqueueSnackbar('Error en carga de datos', { variant: 'error' });
    }
  }, [enqueueSnackbar, isErrorCheckData, t]);

  const handleClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    if (isErrorPdf) {
      enqueueSnackbar(t('modals.error.download_demands'), { variant: 'error' });
    }
    if (pdfRequest && demandPdf?.data) {
      downloadFile(
        demandPdf.data,
        'application/pdf',
        `${demand.reference} - ${format(
          new Date(version ? version.createdAt : demand.versions[0].createdAt),
          'yyyyMMddHHmmss',
        )}.pdf`,
      );
      setPdfRequest(false);
    }
  }, [demandPdf, demand, enqueueSnackbar, isErrorPdf, t, pdfRequest, version]);

  return (
    <Grid item xs={12} align="right" className={classes.grid}>
      {(isAdmin || isManager || isSuperAdmin) && (
        <Button
          className={classes.button}
          color="secondary"
          variant="outlined"
          onClick={() => window.open(`https://platform.orgaconsulting.es/pgo/sol?id=${demand.id}`, '_blank')}

          title='Gestión'
        >
          Gestión
        </Button>
      )}
      {(isAdmin || isManager || isSuperAdmin) && (
        <Button
          className={classes.button}
          color="secondary"
          variant="outlined"
          onClick={handleClickOpen}
          title='Analisis Congruencia'
          disabled={isAddCommentButtonLoading}
        >
          {isAddCommentButtonLoading ? 'Cargando ...' : 'Análisis Congruencia'} 
        </Button>
      )}
      {!isLoadingCheckDataDemand && open && (
          <BootstrapDialog
              onClose={handleClose}
              aria-labelledby="customized-dialog-title"
              open={open}
              maxWidth="lg" 
              fullWidth
          >
            <DialogTitle id="customized-dialog-title">
              {t('demands:buttons.congruenceAnalysis')}
            </DialogTitle>
            <DialogContent dividers>
              <Grid container alignItems="center" spacing={1}>
                <Grid item>
                  <iframe 
                    frameborder="0" allowtransparency 
                    src={`https://reports.orgaconsulting.es/public/dashboard/b55642b3-1368-47d1-8f97-d4d575bfa2f0?id=${demand.id}`}
                    style={{ width: '1200px', height: '700px' }}
                  ></iframe>
                </Grid>
              </Grid>

            </DialogContent>
            <DialogActions>
              <Button autoFocus onClick={handleClose}>
                {t('demands:buttons.close')}
              </Button>
            </DialogActions>
          </BootstrapDialog>
      )}
      {isLoadingCheckDataDemand && open && (
        <Dialog open={open} maxWidth="xs">
            <DialogContent>
              <Loader />
            </DialogContent>
        </Dialog>
        )}
      {isErrorCheckData && open && (
          <AlertDialog
              title={t('modals.error.text_check_data_action')}
              text={t('modals.error.text_check_data_action')}
              type="error"
          />
      )}
      {version && (
        <Button
          color="primary"
          className={classes.button}
          variant="outlined"
          onClick={() => generatePdf(demand, version)}
          title={t('demands:buttons.printPdfVersion')}
        >
          {t('demands:buttons.printPdfVersion')}
        </Button>
      )}
      {demand.status !== STATUS.DRAFT && (
        <Button
          color="primary"
          className={classes.button}
          variant="outlined"
          onClick={() => generatePdf(demand, demand.versions[0])}
          title={t('demands:buttons.printPdf')}
        >
          {t('demands:buttons.printPdf')}
        </Button>
      )}

      <Button
        className={classes.button}
        variant="outlined"
        onClick={goBack}
        title={t('common:buttons.back')}
      >
        {t('common:buttons.back')}
      </Button>
      
    </Grid>
  );
};

DemandDetailsActions.propTypes = {
  demand: PropTypes.object,
  version: PropTypes.object,
};

export default DemandDetailsActions;
